import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "!bg-primary text-primary-foreground hover:bg-primary text-md",
        accent: "bg-orange-500 text-white hover:bg-orange-600",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-primary-foreground hover:bg-secondary",
        ghost: "",
        link: "text-primary underline-offset-4 hover:underline",

        info: "bg-[#3B82F6] text-white hover:bg-[#2563EB]",
        muted: "bg-[#E5E7EB] text-gray-800 hover:bg-[#D1D5DB]",
        indigo: "bg-[#6366F1] text-white hover:bg-[#4F46E5]",
        pink: "bg-[#EC4899] text-white hover:bg-[#DB2777]",
        cyan: "bg-[#06B6D4] text-white hover:bg-[#0891B2]",
        success: "bg-green-600 text-white active:bg-green-500 hover:bg-green-500",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-7 rounded-md px-3 text-xs gap-2",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        action: 'aspect-square h-7 w-7',
        'action-sm': 'aspect-square h-5 w-5',
        'action-xs': 'aspect-square h-4 w-4',
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
